import { useCallback, useEffect, useMemo, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { BoardSocket } from "@/lib/ws";
import { log } from "@/lib/log";
import type { BoardDetail, Cell, CellValue, ColumnDef, Row, UUID, WsEnvelope } from "@/lib/types";

interface CellUpdatePayload {
  row_id: UUID;
  column_def_id: UUID;
  value: CellValue;
  updated_by: UUID | null;
  updated_at: string;
}

/** Insert a row, or REPLACE it if its id is already present, then sort by
 * position. Idempotent — a server row returned by create/duplicate `onSuccess`
 * and the same row arriving via the WS `row.created` echo must not produce two
 * rows with the same id (the B6 double-insert / "deletes mark both" bug). The
 * passed (fuller) row wins over an echo-inserted cells-less one. */
function upsertRowById(rows: Row[], row: Row): Row[] {
  const others = rows.filter((r) => r.id !== row.id);
  return [...others, row].sort((a, b) => a.position - b.position);
}

// Idempotent column upsert by id — the createColumn mutation's onSuccess AND the
// WS `column.created` echo both want to land the new column, and either can win the
// race. A blind append duplicated the column ("adds two columns"); upserting keeps
// it at ONE (B6's row fix, applied to columns).
function upsertColumnById(columns: ColumnDef[], col: ColumnDef): ColumnDef[] {
  const others = columns.filter((c) => c.id !== col.id);
  return [...others, col].sort((a, b) => a.position - b.position);
}

function applyWsEvent(
  prev: BoardDetail | undefined,
  envelope: WsEnvelope,
): BoardDetail | undefined {
  if (!prev) return prev;
  switch (envelope.type) {
    case "row.created": {
      const payload = envelope.payload as Row;
      if (prev.rows.some((r) => r.id === payload.id)) return prev;
      // The row.created broadcast (_row_payload) omits cells for a brand-new
      // row; guarantee the array so downstream consumers never see undefined.
      const row = payload.cells ? payload : { ...payload, cells: [] };
      return { ...prev, rows: [...prev.rows, row].sort((a, b) => a.position - b.position) };
    }
    case "row.updated": {
      const row = envelope.payload as Row;
      const rows = prev.rows.map((r) => (r.id === row.id ? { ...r, ...row } : r));
      rows.sort((a, b) => a.position - b.position);
      return { ...prev, rows };
    }
    case "row.deleted": {
      // Backend broadcasts {id, cascaded_ids} (the deleted row + its children),
      // NOT row_id — remove all of them.
      const { id, cascaded_ids } = envelope.payload as { id: UUID; cascaded_ids?: UUID[] };
      const removed = new Set<string>([id, ...(cascaded_ids ?? [])]);
      return { ...prev, rows: prev.rows.filter((r) => !removed.has(r.id)) };
    }
    case "row.restored": {
      // Undo of a delete: re-add the restored rows (parent + same-batch
      // children), deduped against anything already present.
      const { rows } = envelope.payload as { rows?: Row[] };
      if (!rows?.length) return prev;
      const present = new Set(prev.rows.map((r) => r.id));
      const added = rows.filter((r) => !present.has(r.id));
      if (added.length === 0) return prev;
      return { ...prev, rows: [...prev.rows, ...added].sort((a, b) => a.position - b.position) };
    }
    case "cell.updated": {
      const cell = envelope.payload as CellUpdatePayload;
      return {
        ...prev,
        rows: prev.rows.map((r) =>
          r.id !== cell.row_id
            ? r
            : {
                ...r,
                cells: upsertCell(r.cells, {
                  row_id: cell.row_id,
                  column_def_id: cell.column_def_id,
                  value: cell.value,
                  updated_by: cell.updated_by,
                  updated_at: cell.updated_at,
                }),
              },
        ),
      };
    }
    case "column.created": {
      const column = envelope.payload as ColumnDef;
      return { ...prev, columns: upsertColumnById(prev.columns, column) };
    }
    case "column.updated": {
      const column = envelope.payload as ColumnDef;
      return {
        ...prev,
        columns: prev.columns
          .map((c) => (c.id === column.id ? column : c))
          .sort((a, b) => a.position - b.position),
      };
    }
    case "column.deleted": {
      const { column_id } = envelope.payload as { column_id: UUID };
      return {
        ...prev,
        columns: prev.columns.filter((c) => c.id !== column_id),
        rows: prev.rows.map((r) => ({
          ...r,
          cells: r.cells.filter((cell) => cell.column_def_id !== column_id),
        })),
      };
    }
    case "board.updated": {
      const board = envelope.payload as Partial<BoardDetail>;
      return { ...prev, ...board };
    }
    default:
      return prev;
  }
}

function upsertCell(cells: Cell[], next: Cell): Cell[] {
  const idx = cells.findIndex((c) => c.column_def_id === next.column_def_id);
  if (idx === -1) return [...cells, next];
  const out = cells.slice();
  out[idx] = next;
  return out;
}

// The board-detail endpoint paginates rows by position. A single page only surfaces
// the lowest-position rows (e.g. the first imported group), so we walk the cursor and
// accumulate every page — otherwise large boards (and grouping/kanban over them) only
// ever show a partial first slice.
async function fetchWholeBoard(boardId: UUID): Promise<BoardDetail> {
  const PAGE = 500;
  const first = await api.getBoard(boardId, { limit: PAGE });
  const rows = [...first.rows];
  let cursor = first.next_cursor;
  let hasMore = first.has_more;
  let guard = 0;
  while (hasMore && cursor != null && guard < 50) {
    const next = await api.getBoard(boardId, { cursor, limit: PAGE });
    rows.push(...next.rows);
    cursor = next.next_cursor;
    hasMore = next.has_more;
    guard += 1;
  }
  return { ...first, rows, next_cursor: null, has_more: false };
}

/**
 * The ONE query definition for the board-detail cache key. Any component that
 * observes `queryKeys.board(id)` MUST use this, never its own queryFn: TanStack v5
 * writes every observer's options — disabled ones included — onto the shared Query,
 * and a refetch (e.g. invalidateQueries after "Lösen") runs whichever queryFn was
 * written LAST. A closed CreateFollowUpDialog once registered a single-page
 * `api.getBoard(id)` here, so solving a ticket shrank the IT board to its first 100
 * rows until a reload (2026-09-24).
 */
export function boardDetailQueryOptions(boardId: UUID) {
  return {
    queryKey: queryKeys.board(boardId),
    queryFn: () => fetchWholeBoard(boardId),
  };
}

export function useBoard(boardId: UUID | undefined) {
  const queryClient = useQueryClient();
  const queryKey = boardId ? queryKeys.board(boardId) : ["boards", "void"];

  // Tombstones: ids of rows just deleted on THIS client. The server broadcasts
  // row.created to EVERY client including the actor, so a create-then-delete of
  // a sub-item can have its late row.created echo arrive AFTER the delete and
  // resurrect the row (a phantom that survives until reload). We drop any
  // row.created whose id is tombstoned. TTL-bounded so a genuinely new row that
  // happens to reuse an id later isn't blocked forever.
  const tombstones = useRef<Map<string, number>>(new Map());
  const TOMBSTONE_TTL = 30_000;
  const tombstone = useCallback((ids: string[]) => {
    const until = Date.now() + TOMBSTONE_TTL;
    for (const id of ids) tombstones.current.set(id, until);
  }, []);
  const untombstone = useCallback((ids: string[]) => {
    for (const id of ids) tombstones.current.delete(id);
  }, []);
  const isTombstoned = useCallback((id: string) => {
    const until = tombstones.current.get(id);
    if (until === undefined) return false;
    if (until < Date.now()) {
      tombstones.current.delete(id);
      return false;
    }
    return true;
  }, []);

  const query = useQuery({
    enabled: Boolean(boardId),
    queryKey,
    // Same fetcher as boardDetailQueryOptions — never a single page (see its doc).
    queryFn: () => fetchWholeBoard(boardId as UUID),
  });

  useEffect(() => {
    if (!boardId) return;
    const socket = new BoardSocket(boardId);
    const unsubscribe = socket.subscribe((envelope) => {
      // Presence: live list of viewer user-ids (Redis-aggregated server-side).
      if (envelope.type === "presence.update") {
        const ids = (envelope.payload as { user_ids?: string[] }).user_ids ?? [];
        queryClient.setQueryData(["boards", boardId, "presence"], ids);
        return;
      }
      // Comments aren't part of board-detail cache. Invalidate the row-scoped
      // comments query so an open RowDetailPanel reloads its timeline.
      if (
        envelope.type === "comment.created" ||
        envelope.type === "comment.deleted" ||
        envelope.type === "comment.updated"
      ) {
        const rowId = (envelope.payload as { row_id?: UUID }).row_id;
        if (rowId) {
          void queryClient.invalidateQueries({ queryKey: ["rows", rowId, "comments"] });
        }
        return;
      }
      // Activity timeline (Phase 3): these mutations land in the events outbox.
      // Refresh the row-scoped activity query so an open detail panel's
      // "Verlauf" updates live. Board-detail cache is still updated by
      // applyWsEvent below — this only adds the activity-query refresh.
      if (
        envelope.type === "cell.updated" ||
        envelope.type === "row.created" ||
        envelope.type === "row.deleted" ||
        envelope.type === "row.restored"
      ) {
        // Each event carries the row id differently: cell.updated/row.created →
        // {row_id|id}; row.deleted → {id, cascaded_ids[]}; row.restored →
        // {rows:[{id}]}. Collect them all so an open detail panel on any
        // affected row (incl. a restored child) refreshes its Verlauf.
        const p = envelope.payload as {
          row_id?: UUID;
          id?: UUID;
          cascaded_ids?: UUID[];
          rows?: { id: UUID }[];
        };
        const rids = new Set<UUID>();
        if (p.row_id) rids.add(p.row_id);
        if (p.id) rids.add(p.id);
        for (const c of p.cascaded_ids ?? []) rids.add(c);
        for (const r of p.rows ?? []) rids.add(r.id);
        for (const rid of rids)
          void queryClient.invalidateQueries({ queryKey: ["rows", rid, "activity"] });
        // Cycle-time bar (Zeitleiste) is board-level → refresh once per event.
        if (boardId)
          void queryClient.invalidateQueries({
            queryKey: queryKeys.boardStatusSegments(boardId),
          });
      }
      // Ignore a row.created echo for a row we just deleted (the resurrection
      // race). Track deletes/restores so the tombstone set stays current.
      if (envelope.type === "row.created") {
        if (isTombstoned((envelope.payload as Row).id)) return;
      } else if (envelope.type === "row.deleted") {
        const p = envelope.payload as { id: UUID; cascaded_ids?: UUID[] };
        tombstone([p.id, ...(p.cascaded_ids ?? [])]);
      } else if (envelope.type === "row.restored") {
        untombstone(((envelope.payload as { rows?: Row[] }).rows ?? []).map((r) => r.id));
      }
      queryClient.setQueryData<BoardDetail | undefined>(queryKey, (prev) =>
        applyWsEvent(prev, envelope),
      );
    });
    void socket.connect();
    return () => {
      unsubscribe();
      socket.close();
    };
    // queryKey is derived from boardId; including queryClient is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId, queryClient]);

  const createRow = useMutation({
    mutationFn: (
      vars: { afterRowId?: UUID | null; parentRowId?: UUID | null } | undefined = undefined,
    ) =>
      api.createRow(boardId as UUID, {
        ...(vars?.afterRowId ? { after_row_id: vars.afterRowId } : {}),
        ...(vars?.parentRowId ? { parent_row_id: vars.parentRowId } : {}),
      }),
    onSuccess: (row) => {
      // Dedup by id: the server also broadcasts row.created over WS to this
      // client, and that echo may land before/around onSuccess. Without the
      // upsert we'd append a 2nd row with the SAME id (B6 → flaky delete B2).
      queryClient.setQueryData<BoardDetail | undefined>(queryKey, (prev) =>
        prev ? { ...prev, rows: upsertRowById(prev.rows, row) } : prev,
      );
    },
  });

  const duplicateRow = useMutation({
    mutationFn: (vars: {
      rowId: UUID;
      withComments?: boolean;
      // Cross-group / under-parent copy (absent = copy in place, same group).
      targetParentRowId?: UUID | null;
      groupColumnId?: UUID | null;
      groupValue?: unknown;
    }) =>
      api.duplicateRow(vars.rowId, {
        withComments: vars.withComments,
        targetParentRowId: vars.targetParentRowId,
        groupColumnId: vars.groupColumnId,
        groupValue: vars.groupValue,
      }),
    onSuccess: (row) => {
      // Server mints id/position/cells — insert the returned row, deduped against
      // the WS row.created echo (no optimistic step; ids aren't predictable).
      queryClient.setQueryData<BoardDetail | undefined>(queryKey, (prev) =>
        prev ? { ...prev, rows: upsertRowById(prev.rows, row) } : prev,
      );
    },
  });

  const duplicateGroup = useMutation({
    mutationFn: (vars: {
      groupColumnId: UUID;
      sourceValue: unknown;
      newLabel: string;
      withComments?: boolean;
    }) =>
      api.duplicateGroup(boardId as UUID, {
        group_column_id: vars.groupColumnId,
        source_value: vars.sourceValue,
        new_label: vars.newLabel,
        with_comments: vars.withComments ?? false,
      }),
    // A new grouping option + copied rows: refetch the board so the new section renders.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const updateCell = useMutation({
    mutationFn: async (vars: { rowId: UUID; columnDefId: UUID; value: CellValue }) =>
      api.upsertCell(vars.rowId, vars.columnDefId, { value: vars.value }),
    onMutate: (vars) => {
      // Fire-and-forget cancellation; awaiting it makes the optimistic
      // setQueryData async, which causes a transient stale render between
      // mutate() and the optimistic write (visible as flicker / snap-back).
      void queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<BoardDetail | undefined>(queryKey);
      queryClient.setQueryData<BoardDetail | undefined>(queryKey, (b) =>
        b
          ? {
              ...b,
              rows: b.rows.map((r) =>
                r.id !== vars.rowId
                  ? r
                  : {
                      ...r,
                      cells: upsertCell(r.cells, {
                        row_id: vars.rowId,
                        column_def_id: vars.columnDefId,
                        value: vars.value,
                        updated_by: null,
                        updated_at: new Date().toISOString(),
                      }),
                    },
              ),
            }
          : b,
      );
      return { prev };
    },
    onSuccess: (_data, vars) => {
      // Person-cell writes can auto-add the assigned user to the workspace
      // (backend `ensure_members_for_user_ids`) AND change the actor's
      // assigned-count Dashboard stat. Invalidate the dependent queries so
      // the new avatar resolves + the stat refreshes without a manual reload.
      const board = queryClient.getQueryData<BoardDetail | undefined>(queryKey);
      const col = board?.columns.find((c) => c.id === vars.columnDefId);
      if (col?.type === "person" && board) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.workspaceMembers(board.workspace_id),
        });
        void queryClient.invalidateQueries({ queryKey: queryKeys.assignedCount });
        // Assigning a non-member grants them guest access — refresh the board
        // people directory so their avatar/name resolves in person cells.
        void queryClient.invalidateQueries({ queryKey: ["boards", boardId, "people"] });
      }
    },
    onError: (err, _vars, ctx) => {
      log.error("Cell update failed", err);
      if (ctx?.prev) queryClient.setQueryData(queryKey, ctx.prev);
    },
  });

  const createColumn = useMutation({
    mutationFn: (payload: Parameters<typeof api.createColumn>[1]) =>
      api.createColumn(boardId as UUID, payload),
    onSuccess: (col) => {
      // Upsert by id — NOT a blind append: the backend broadcasts `column.created`
      // over WS and the actor gets their own echo. If that echo lands before this
      // onSuccess (common — the broadcast often beats the HTTP response handler),
      // the column is already in the cache and appending again would duplicate it
      // (the "adds two columns" bug — same class as the B6 row double-insert).
      queryClient.setQueryData<BoardDetail | undefined>(queryKey, (b) =>
        b ? { ...b, columns: upsertColumnById(b.columns, col) } : b,
      );
    },
  });

  const addColumnOption = useMutation({
    mutationFn: (vars: { column: ColumnDef; label: string; color?: string }) =>
      api.addColumnOption(vars.column, vars.label, vars.color),
    onSuccess: (updated) => {
      // Write the server-returned column straight into the board cache so the
      // new option is visible in pickers immediately. A bare refetch() is
      // async and race-prone (the popover re-renders from stale cache first).
      queryClient.setQueryData<BoardDetail | undefined>(queryKey, (b) =>
        b
          ? {
              ...b,
              columns: b.columns
                .map((c) => (c.id === updated.id ? updated : c))
                .sort((a, b) => a.position - b.position),
            }
          : b,
      );
    },
    onError: (err) => {
      log.error("Add column option failed", err);
    },
  });

  const updateRowPosition = useMutation({
    mutationFn: (vars: { rowId: UUID; position: number }) =>
      api.updateRow(vars.rowId, { position: vars.position }),
    onMutate: (vars) => {
      void queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<BoardDetail | undefined>(queryKey);
      queryClient.setQueryData<BoardDetail | undefined>(queryKey, (b) =>
        b
          ? {
              ...b,
              rows: b.rows
                .map((r) => (r.id === vars.rowId ? { ...r, position: vars.position } : r))
                .sort((a, b) => a.position - b.position),
            }
          : b,
      );
      return { prev };
    },
    onError: (err, _vars, ctx) => {
      log.error("Row position update failed", err);
      if (ctx?.prev) queryClient.setQueryData(queryKey, ctx.prev);
    },
  });

  // Reparent/promote a sub-item: PATCH parent_row_id + position TOGETHER so the
  // move is atomic (dropping into a new parent AND landing at the right slot are
  // one write). Optimistic + rollback like updateRowPosition; the WS row.updated
  // echo (applyWsEvent) reconciles parent/depth/position for free.
  const moveRow = useMutation({
    mutationFn: (vars: { rowId: UUID; parentRowId: UUID | null; position: number }) =>
      api.updateRow(vars.rowId, { parent_row_id: vars.parentRowId, position: vars.position }),
    onMutate: (vars) => {
      void queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<BoardDetail | undefined>(queryKey);
      queryClient.setQueryData<BoardDetail | undefined>(queryKey, (b) =>
        b
          ? {
              ...b,
              rows: b.rows
                .map((r) =>
                  r.id === vars.rowId
                    ? {
                        ...r,
                        parent_row_id: vars.parentRowId,
                        // MAX_ROW_DEPTH=1: a child's parent is always top-level.
                        depth: vars.parentRowId ? 1 : 0,
                        position: vars.position,
                      }
                    : r,
                )
                .sort((a, b) => a.position - b.position),
            }
          : b,
      );
      return { prev };
    },
    onError: (err, _vars, ctx) => {
      log.error("Row move failed", err);
      if (ctx?.prev) queryClient.setQueryData(queryKey, ctx.prev);
    },
  });

  const deleteRow = useMutation({
    mutationFn: (vars: { rowId: UUID }) => api.deleteRow(vars.rowId),
    onMutate: (vars) => {
      void queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<BoardDetail | undefined>(queryKey);
      // Tombstone the row + its children NOW, so a late row.created echo of a
      // just-created sub-item can't resurrect it before the WS row.deleted lands.
      const childIds = (prev?.rows ?? [])
        .filter((r) => r.parent_row_id === vars.rowId)
        .map((r) => r.id);
      tombstone([vars.rowId, ...childIds]);
      // Optimistically drop the row + its direct children (depth cap = 1).
      queryClient.setQueryData<BoardDetail | undefined>(queryKey, (b) =>
        b
          ? {
              ...b,
              rows: b.rows.filter((r) => r.id !== vars.rowId && r.parent_row_id !== vars.rowId),
            }
          : b,
      );
      return { prev };
    },
    onError: (err, _vars, ctx) => {
      log.error("Row delete failed", err);
      if (ctx?.prev) queryClient.setQueryData(queryKey, ctx.prev);
    },
  });

  const restoreRow = useMutation({
    // `rows` is the caller's snapshot of what delete removed, so undo re-adds
    // the FULL rows (with cells) optimistically; the server + WS reconcile.
    mutationFn: (vars: { rowId: UUID; rows: Row[] }) => api.restoreRow(vars.rowId),
    onMutate: (vars) => {
      void queryClient.cancelQueries({ queryKey });
      // Lift the tombstones so the restored rows are allowed back in.
      untombstone(vars.rows.map((r) => r.id));
      const prev = queryClient.getQueryData<BoardDetail | undefined>(queryKey);
      const addedIds: string[] = [];
      queryClient.setQueryData<BoardDetail | undefined>(queryKey, (b) => {
        if (!b) return b;
        const present = new Set(b.rows.map((r) => r.id));
        const added = vars.rows.filter((r) => !present.has(r.id));
        if (added.length === 0) return b;
        addedIds.push(...added.map((r) => r.id));
        return { ...b, rows: [...b.rows, ...added].sort((a, c) => a.position - c.position) };
      });
      return { prev, addedIds };
    },
    onSuccess: (res, vars, ctx) => {
      // The server restores the row and only the sub-items of the SAME delete batch; a
      // sub-item deleted in its own batch stays deleted. Drop the optimistically re-added
      // rows it did not restore, or they stay in the table until a reload. An answer
      // without the row itself is the no-op (the row was live already, restored by a
      // colleague or another tab): it does not say which sub-items are live, so the
      // cache is left as it is.
      const restored = new Set<string>(res.restored);
      if (!restored.has(vars.rowId)) return;
      const notRestored = new Set((ctx?.addedIds ?? []).filter((id) => !restored.has(id)));
      if (notRestored.size === 0) return;
      queryClient.setQueryData<BoardDetail | undefined>(queryKey, (b) =>
        b ? { ...b, rows: b.rows.filter((r) => !notRestored.has(r.id)) } : b,
      );
    },
    onError: (err, _vars, ctx) => {
      log.error("Row restore failed", err);
      if (ctx?.prev) queryClient.setQueryData(queryKey, ctx.prev);
    },
  });

  const updateColumn = useMutation({
    mutationFn: (vars: { columnId: UUID; payload: Parameters<typeof api.updateColumn>[1] }) =>
      api.updateColumn(vars.columnId, vars.payload),
    onMutate: (vars) => {
      void queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<BoardDetail | undefined>(queryKey);
      queryClient.setQueryData<BoardDetail | undefined>(queryKey, (b) =>
        b
          ? {
              ...b,
              columns: b.columns
                .map((c) =>
                  c.id !== vars.columnId
                    ? c
                    : {
                        ...c,
                        ...(vars.payload.position !== undefined && vars.payload.position !== null
                          ? { position: vars.payload.position }
                          : {}),
                        ...(vars.payload.config !== undefined && vars.payload.config !== null
                          ? { config: { ...c.config, ...vars.payload.config } }
                          : {}),
                        ...(vars.payload.name !== undefined && vars.payload.name !== null
                          ? { name: vars.payload.name }
                          : {}),
                      },
                )
                .sort((a, b) => a.position - b.position),
            }
          : b,
      );
      return { prev };
    },
    onError: (err, _vars, ctx) => {
      log.error("Column update failed", err);
      if (ctx?.prev) queryClient.setQueryData(queryKey, ctx.prev);
    },
  });

  return useMemo(
    () => ({
      ...query,
      createRow,
      updateCell,
      createColumn,
      addColumnOption,
      updateRowPosition,
      moveRow,
      updateColumn,
      deleteRow,
      restoreRow,
      duplicateRow,
      duplicateGroup,
    }),
    [
      query,
      createRow,
      updateCell,
      createColumn,
      addColumnOption,
      updateRowPosition,
      moveRow,
      updateColumn,
      deleteRow,
      restoreRow,
      duplicateRow,
      duplicateGroup,
    ],
  );
}

export const __testing = { applyWsEvent, upsertCell, upsertRowById, upsertColumnById };
